import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Col, Row } from "react-bootstrap";
import {
  FaBuilding,
  FaBullseye,
  FaGlobe,
  FaIdBadge,
  FaIndustry,
  FaInfoCircle,
  FaLink,
  FaUserCheck,
} from "react-icons/fa";
import { User } from "../../types/Types";
import { getConnectionMembershipDisplay } from "../../utils/connectionUserUtils";
import { OBJECT_FIT_COVER } from "./profileModalHelpers";

interface ProfileModalBusinessDetailsProps {
  selectedUser: User;
  isConnected?: boolean;
}

function ProfileModalBusinessDetails({
  selectedUser,
  isConnected,
}: ProfileModalBusinessDetailsProps) {
  const { t } = useTranslation();

  if (isConnected) {
    return (
      <div className="p-3">
        <hr className="mt-0" />
        <h5 className="fw-semibold mb-3">{t("profile.section.businessDetails")}</h5>
        <Row>
          <Col md={4} className="mb-3 d-block d-md-none">
            <div className="d-flex align-items-start">
              <FaBuilding
                size={20}
                className="me-2 mt-1 text-secondary"
              />
              <div className="d-flex justify-content-between w-100">
                <div>
                  <strong>{t("profile.label.businessName")}</strong>
                  <div className="text-muted small">
                    {selectedUser.businessName ||
                      t("profile.notProvidedYet")}
                  </div>
                </div>
                {selectedUser.BusinessLogo && (
                  <img
                    src={selectedUser.BusinessLogo}
                    className="rounded-circle me-2 mt-1"
                    width={40}
                    height={40}
                    alt={
                      selectedUser.businessName
                        ? t("profile.alt.businessLogoFor", {
                            name: selectedUser.businessName,
                          })
                        : t("profile.alt.businessLogo")
                    }
                    loading="lazy"
                    decoding="async"
                    style={OBJECT_FIT_COVER}
                  />
                )}
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaIndustry
                size={20}
                className="me-2 mt-1 text-secondary"
              />
              <div>
                <strong>{t("profile.label.sectorIndustry")}</strong>
                <div className="text-muted small">
                  {selectedUser.businessSectorIndustry ||
                    t("profile.notProvidedYet")}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaGlobe
                size={20}
                className="me-2 mt-1 text-secondary"
              />
              <div>
                <strong>{t("profile.label.countriesOperation")}</strong>
                <div className="text-muted small">
                  {selectedUser.businessCountryOperation ||
                    t("profile.notProvidedYet")}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaInfoCircle
                size={20}
                className="me-2 mt-1 text-secondary"
              />
              <div>
                <strong>{t("profile.label.aboutBusiness")}</strong>
                <div className="text-muted small">
                  {selectedUser.businessAbout ||
                    t("profile.notProvidedYet")}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaLink
                size={20}
                className="me-2 mt-1 text-secondary flex-shrink-0"
              />
              <div className="d-flex flex-column w-100">
                <strong>{t("profile.label.websiteUrl")}</strong>
                <a
                  href={selectedUser.businessWebsiteUrl || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted small text-truncate pr-1"
                >
                  {selectedUser.businessWebsiteUrl ||
                    t("profile.notProvidedYet")}
                </a>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaBullseye
                size={20}
                className="me-2 mt-1 text-secondary"
              />
              <div>
                <strong>{t("profile.label.objectives")}</strong>
                <div className="text-muted small">
                  {selectedUser.businessObjectives ||
                    t("profile.notProvidedYet")}
                </div>
              </div>
            </div>
          </Col>

          <Col md={4} className="mb-3">
            <div className="d-flex align-items-start">
              <FaIdBadge
                size={20}
                className="me-2 mt-1 text-secondary"
              />

              <div>
                <strong>{t("profile.label.membershipTypeCap")}</strong>
                <div className="text-muted small">
                  {getConnectionMembershipDisplay(selectedUser) ||
                    selectedUser.membershipName ||
                    t("profile.notProvidedYet")}
                </div>
              </div>
            </div>
          </Col>
        </Row>
        <hr />
        <h5 className="fw-semibold mb-3">{t("profile.section.discounts")}</h5>
        <Row>
          <Col md={6} className="mb-3">
            <div className="d-flex align-items-start">
              <FaIdBadge
                size={20}
                className="me-2 mt-1 text-secondary"
              />
              <div>
                <strong>{t("profile.label.productsServices")}</strong>
                <div className="text-muted small">
                  {selectedUser.businessProductServices ||
                    t("profile.notProvidedYet")}
                </div>
              </div>
            </div>
          </Col>
          <Col md={6} className="mb-3">
            <div className="d-flex align-items-start">
              <FaUserCheck
                size={20}
                className="me-2 mt-1 text-secondary"
              />
              <div>
                <strong>{t("profile.label.discountsPlatform")}</strong>
                <div className="text-muted small">
                  {selectedUser.businessDiscount ||
                    t("profile.notProvidedYet")}
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="p-3 d-block d-md-none">
      <hr />
      <h5 className="fw-semibold mb-3">{t("profile.section.businessDetails")}</h5>
      <Row>
        <Col md={4} className="mb-3 ">
          <div className="d-flex align-items-start">
            <FaBuilding
              size={20}
              className="me-2 mt-1 text-secondary"
            />
            <div className="d-flex justify-content-between w-100">
              <div>
                <strong>{t("profile.label.businessName")}</strong>
                <div className="text-muted small">
                  {selectedUser.businessName ||
                    t("profile.notProvidedYet")}
                </div>
              </div>
              {selectedUser.BusinessLogo ? (
                <img
                  src={selectedUser.BusinessLogo}
                  className="rounded-circle me-2 mt-1"
                  width={40}
                  height={40}
                  alt={
                    selectedUser.businessName
                      ? t("profile.alt.businessLogoFor", {
                          name: selectedUser.businessName,
                        })
                      : t("profile.alt.businessLogo")
                  }
                  loading="lazy"
                  decoding="async"
                  style={OBJECT_FIT_COVER}
                />
              ) : null}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
}

export default memo(ProfileModalBusinessDetails);
